import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, addDays, addMonths, differenceInCalendarMonths } from 'date-fns';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getUserBookings, cancelBooking, getManualBlocksForUser, getMemberCreditBalance, getSubMembers, saveSubMembers, createGuestBooking, getGuestBookings, revokeGuestBooking, getTeamRoster, addTeamRosterMember, removeTeamRosterMember } from '../services/bookingService';
import { doc, getDoc, updateDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { verifyBeforeUpdateEmail } from 'firebase/auth';
import { auth } from '../services/firebase';
import { sendCancellationEmail, sendSubMemberWelcome, sendAccessCodeNotification } from '../services/emailService';
import { getAccessFeatureEnabled, generateUniquePin } from '../services/accessService';
import { sendPhoneOtp, verifyPhoneOtp } from '../services/smsService';
import { getNotificationPrefs, saveNotificationPrefs, DEFAULT_PREFS, PREF_LABELS } from '../services/notificationPrefsService';
import { useSpaces } from '../contexts/SpacesContext';

const normalisePhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.trim().replace(/[\s\-().]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.startsWith('0')) return '+61' + cleaned.slice(1);
  return cleaned;
};

const statusColors = {
  confirmed: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const statusLabels = {
  confirmed: 'Confirmed',
  pending: 'Pending',
  cancelled: 'Cancelled',
};

// Generate payment schedule (matches admin logic)
const generatePaymentSchedule = (booking) => {
  const start = booking.startDate?.toDate ? booking.startDate.toDate() : new Date(booking.startDate);
  const end = booking.endDate?.toDate ? booking.endDate.toDate() : new Date(booking.endDate);
  const existing = booking.paymentSchedule || [];

  if (booking.pricingType === 'monthly') {
    const numMonths = Math.max(1, differenceInCalendarMonths(end, start));
    const unitAmount = booking.unitPrice || (numMonths > 0 ? Math.round(booking.price / numMonths) : booking.price);
    return Array.from({ length: numMonths }, (_, i) => {
      const dueDate = addMonths(start, i);
      const id = format(dueDate, 'yyyy-MM');
      return existing.find((e) => e.id === id) || {
        id, dueDate: format(dueDate, 'yyyy-MM-dd'), label: format(dueDate, 'MMMM yyyy'),
        amount: unitAmount, paid: false, paidAt: null,
      };
    });
  }
  return [existing.find((e) => e.id === 'single') || {
    id: 'single',
    dueDate: format(start, 'yyyy-MM-dd'),
    label: start.toDateString() === addDays(end, -1).toDateString()
      ? format(start, 'd MMM yyyy')
      : `${format(start, 'd MMM')} – ${format(addDays(end, -1), 'd MMM yyyy')}`,
    amount: booking.price, paid: false, paidAt: null,
  }];
};

const BookingCard = ({ booking, onCancel, spaces, accessCodesEnabled, teamRoster, guestBookings, setGuestBookings, wifiProfile, userProfile, currentUser, setActiveTab }) => {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const space = spaces.find((s) => s.id === booking.spaceId);
  const now = new Date();
  const start = booking.startDate?.toDate ? booking.startDate.toDate() : new Date(booking.startDate);
  const end = booking.endDate?.toDate ? booking.endDate.toDate() : new Date(booking.endDate);
  const isPast = end < now;

  // Cancellation eligibility
  let canCancel = false;
  let cancelBlockReason = null;
  if (booking.status !== 'cancelled' && !isPast) {
    if (booking.pricingType === 'monthly') {
      // Can cancel only after the current month term ends
      const monthsElapsed = differenceInCalendarMonths(now, start);
      const currentTermEnd = addMonths(start, monthsElapsed + 1);
      if (now >= currentTermEnd) {
        canCancel = true;
      } else {
        cancelBlockReason = `Current term runs until ${format(currentTermEnd, 'd MMM yyyy')}`;
      }
    } else {
      // Daily/hourly: can cancel only if not yet started
      if (start > now) {
        canCancel = true;
      } else {
        cancelBlockReason = 'Booking has already started';
      }
    }
  }

  const showPaymentSchedule = booking.paymentMethod === 'bank_deposit';
  const schedule = showPaymentSchedule ? generatePaymentSchedule(booking) : null;
  const showCardPayment = booking.paymentMethod === 'card';
  const maxTeam = space?.maxSubMembers || 0;

  return (
    <div className="card p-5">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: space?.bgColor || '#f1f5f9' }}>
          <span className="text-lg" style={{ color: space?.color }}>{space?.type === 'room' ? '🏢' : '💻'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold text-slate-800">{space?.name || booking.spaceId}</h3>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[booking.status] || 'bg-gray-100 text-gray-500'}`}>
              {statusLabels[booking.status] || booking.status}
            </span>
          </div>
          <p className="text-sm text-slate-500">
            {booking.pricingType === 'hourly'
              ? `${format(start, 'EEE d MMM, h:mm a')} – ${format(end, 'h:mm a')}`
              : start.toDateString() === addDays(end, -1).toDateString()
                ? format(start, 'd MMM yyyy')
                : `${format(start, 'd MMM yyyy')} → ${format(addDays(end, -1), 'd MMM yyyy')}`}
            {booking.pricingType && <span className="ml-2 text-slate-400">· {booking.pricingType}</span>}
          </p>
          {booking.seatName && <p className="text-xs text-slate-400 mt-0.5">{booking.seatName}</p>}
          {booking.accessPin && booking.status === 'confirmed' && !isPast && (
            <div className="mt-1 inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1">
              <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <span className="text-xs text-slate-500">Door PIN:</span>
              <span className="text-sm font-bold font-mono tracking-widest text-slate-800">{booking.accessPin}</span>
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full font-medium ${
                booking.accessCodeStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-600'
              }`}>
                {booking.accessCodeStatus === 'active' ? 'active' : 'activates 1hr before'}
              </span>
            </div>
          )}
          {booking.price && (
            <p className="text-xs text-slate-400 mt-0.5">
              ${booking.unitPrice || booking.price}
              {booking.pricingType === 'monthly' ? '/month' : booking.pricingType === 'daily' ? '/day' : '/hr'}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {canCancel && (
            <button onClick={() => onCancel(booking.id)} className="text-sm text-red-500 hover:text-red-700 font-medium">
              Cancel
            </button>
          )}
          {cancelBlockReason && booking.status !== 'cancelled' && !isPast && (
            <p className="text-xs text-slate-400 text-right">{cancelBlockReason}</p>
          )}
        </div>
      </div>

      {/* Payment status for card bookings */}
      {showCardPayment && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-slate-500">Card Payment</span>
            {booking.paymentStatus === 'paid' ? (
              <span className="flex items-center gap-1 text-green-600 font-medium">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                Paid
                {booking.stripePaymentIntentId && (
                  <span className="text-slate-400 font-normal ml-1 font-mono">· {booking.stripePaymentIntentId}</span>
                )}
              </span>
            ) : (
              <span className="text-amber-600 font-medium">Awaiting Payment</span>
            )}
          </div>
        </div>
      )}

      {/* Payment schedule for bank deposit bookings */}
      {showPaymentSchedule && schedule && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={() => setScheduleOpen((o) => !o)}
            className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            <svg className={`w-3.5 h-3.5 transition-transform ${scheduleOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            Payment Schedule
            <span className="text-slate-400 font-normal">
              ({schedule.filter((p) => p.paid).length}/{schedule.length} paid)
            </span>
          </button>
          {scheduleOpen && (
            <div className="mt-2 space-y-1.5">
              {schedule.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">{payment.label}</span>
                  <span className={`font-medium flex items-center gap-1 ${payment.paid ? 'text-green-600' : 'text-amber-600'}`}>
                    {payment.paid ? (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        Paid{payment.paidDate && <span className="font-normal ml-1">{payment.paidDate}</span>}
                      </>
                    ) : 'Pending'}
                    <span className="text-slate-400 font-normal ml-1">${payment.amount}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Team Members — inline on eligible bookings */}
      {maxTeam > 0 && booking.status === 'confirmed' && !isPast && accessCodesEnabled && (() => {
        const guests = (guestBookings?.[booking.id] || []).filter((g) => g.status !== 'cancelled');
        const assignedIds = guests.map((g) => g.teamMemberId).filter(Boolean);
        const unassigned = (teamRoster || []).filter((m) => !assignedIds.includes(m.id));

        const handleAssign = async (member) => {
          if (guests.length >= maxTeam) { toast.error(`Maximum ${maxTeam} team members per booking.`); return; }
          setAssigning(true);
          try {
            const pin = await generateUniquePin();
            const guestId = await createGuestBooking({
              parentBookingId: booking.id, parentUserId: currentUser.uid,
              teamMemberId: member.id,
              firstName: member.firstName, lastName: member.lastName || '',
              email: member.email, phone: member.phone || '',
              accessPin: pin, spaceId: booking.spaceId,
              startDate: booking.startDate, endDate: booking.endDate,
            });
            setGuestBookings((prev) => ({
              ...prev,
              [booking.id]: [...(prev[booking.id] || []), {
                id: guestId, type: 'guest', teamMemberId: member.id,
                firstName: member.firstName, lastName: member.lastName || '',
                email: member.email, phone: member.phone || '',
                accessPin: pin, accessCodeStatus: 'pending', status: 'confirmed', parentBookingId: booking.id,
              }],
            }));
            toast.success(`${member.firstName} assigned with PIN ${pin}.`);
          } catch { toast.error('Failed to assign team member.'); }
          setAssigning(false);
        };

        const handleSendPin = async (guest) => {
          if (!guest.accessPin || !guest.email) return;
          try {
            await sendAccessCodeNotification({
              to: guest.email,
              name: `${guest.firstName || ''} ${guest.lastName || ''}`.trim() || guest.email,
              phone: guest.phone || '', pin: guest.accessPin,
              spaceName: space?.name || '',
              startDate: format(start, 'EEE d MMM yyyy'), endDate: format(addDays(end, -1), 'EEE d MMM yyyy'),
              bookingId: guest.id,
            });
            toast.success(`PIN sent to ${guest.email}.`);
          } catch { toast.error('Failed to send PIN.'); }
        };

        const handleRevoke = async (guestId) => {
          if (!window.confirm('Revoke door access for this booking?')) return;
          try {
            await revokeGuestBooking(guestId);
            setGuestBookings((prev) => ({
              ...prev, [booking.id]: (prev[booking.id] || []).map((g) =>
                g.id === guestId ? { ...g, status: 'cancelled', accessCodeStatus: 'revoked' } : g),
            }));
            toast.success('Door access revoked.');
          } catch { toast.error('Failed to revoke.'); }
        };

        return (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={() => setTeamOpen((o) => !o)}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              <svg className={`w-3.5 h-3.5 transition-transform ${teamOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Team Access
              <span className="text-slate-400 font-normal">({guests.length}/{maxTeam})</span>
            </button>

            {teamOpen && (
              <div className="mt-3 space-y-3">
                {/* Assigned members */}
                {guests.length > 0 && (
                  <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                    {guests.map((guest) => (
                      <div key={guest.id} className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-slate-500">{guest.firstName?.[0]?.toUpperCase() || '?'}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700">{guest.firstName} {guest.lastName || ''}</p>
                            <p className="text-xs text-slate-400">{guest.email}</p>
                          </div>
                          <button onClick={() => handleRevoke(guest.id)} className="text-xs text-red-400 hover:text-red-600">Revoke</button>
                        </div>
                        {guest.accessPin && (
                          <div className="mt-1.5 ml-10 flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-400">
                              PIN: <span className="font-mono font-bold tracking-widest text-slate-600">{guest.accessPin}</span>
                            </span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${{
                              pending: 'bg-yellow-100 text-yellow-700',
                              provisioned: 'bg-blue-100 text-blue-700',
                              active: 'bg-green-100 text-green-700',
                            }[guest.accessCodeStatus] || 'bg-gray-100 text-gray-500'}`}>
                              {guest.accessCodeStatus === 'pending' ? 'Pending' :
                               guest.accessCodeStatus === 'provisioned' ? 'Provisioned' :
                               guest.accessCodeStatus === 'active' ? 'Active' : guest.accessCodeStatus}
                            </span>
                            <button onClick={() => handleSendPin(guest)} className="text-xs text-brand-500 hover:text-brand-700 underline">Send</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Assign from roster */}
                {unassigned.length > 0 && guests.length < maxTeam && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-500 mb-2">Assign team member</p>
                    <div className="space-y-1.5">
                      {unassigned.map((m) => (
                        <div key={m.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                          <div>
                            <p className="text-sm text-slate-700">{m.firstName} {m.lastName || ''}</p>
                            <p className="text-xs text-slate-400">{m.email}</p>
                          </div>
                          <button
                            onClick={() => handleAssign(m)}
                            disabled={assigning}
                            className="text-xs text-brand-500 hover:text-brand-700 font-medium disabled:opacity-50"
                          >
                            {assigning ? '…' : 'Assign'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(teamRoster || []).length === 0 && (
                  <p className="text-xs text-slate-400 text-center">
                    No team members yet. Add people in the <button onClick={() => setActiveTab('team')} className="text-brand-500 underline">My Team</button> tab first.
                  </p>
                )}

                {guests.length >= maxTeam && (
                  <p className="text-xs text-slate-400 text-center">Maximum of {maxTeam} team members assigned.</p>
                )}
              </div>
            )}
          </div>
        );
      })()}

    </div>
  );
};


// ─── Team Roster Tab ──────────────────────────────────────────────────────────

const TeamRosterTab = ({ currentUser, teamRoster, setTeamRoster, setGuestBookings }) => {
  const [rosterForm, setRosterForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [adding, setAdding] = useState(false);

  const handleAddRoster = async (e) => {
    e.preventDefault();
    if (!rosterForm.firstName || !rosterForm.email) { toast.error('Name and email required.'); return; }
    setAdding(true);
    try {
      const id = await addTeamRosterMember(currentUser.uid, {
        firstName: rosterForm.firstName,
        lastName: rosterForm.lastName || '',
        email: rosterForm.email,
        phone: rosterForm.phone || '',
      });
      setTeamRoster((prev) => [...prev, { id, ...rosterForm, createdAt: new Date() }]);
      setRosterForm({ firstName: '', lastName: '', email: '', phone: '' });
      toast.success(`${rosterForm.firstName} added to your team.`);
    } catch { toast.error('Failed to add team member.'); }
    setAdding(false);
  };

  const handleRemoveRoster = async (member) => {
    if (!window.confirm(`Remove ${member.firstName} ${member.lastName || ''}? Any active door access will be revoked.`)) return;
    try {
      await removeTeamRosterMember(currentUser.uid, member.id);
      setTeamRoster((prev) => prev.filter((m) => m.id !== member.id));
      setGuestBookings((prev) => {
        const updated = { ...prev };
        for (const key of Object.keys(updated)) {
          updated[key] = updated[key].map((g) =>
            g.teamMemberId === member.id ? { ...g, status: 'cancelled', accessCodeStatus: 'revoked' } : g
          );
        }
        return updated;
      });
      toast.success('Team member removed.');
    } catch { toast.error('Failed to remove team member.'); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-800">My Team</h2>
        <p className="text-sm text-slate-500">
          Manage your team members here. You can then assign them door access on individual bookings.
        </p>
      </div>

      <form onSubmit={handleAddRoster} className="card p-5 space-y-3">
        <h3 className="font-semibold text-slate-700 text-sm">Add Team Member</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label text-xs">First Name *</label>
            <input className="input-field py-1.5 text-sm" placeholder="Jane" value={rosterForm.firstName}
              onChange={(e) => setRosterForm((f) => ({ ...f, firstName: e.target.value }))} required />
          </div>
          <div>
            <label className="label text-xs">Last Name</label>
            <input className="input-field py-1.5 text-sm" placeholder="Smith" value={rosterForm.lastName}
              onChange={(e) => setRosterForm((f) => ({ ...f, lastName: e.target.value }))} />
          </div>
          <div>
            <label className="label text-xs">Email *</label>
            <input type="email" className="input-field py-1.5 text-sm" placeholder="jane@example.com" value={rosterForm.email}
              onChange={(e) => setRosterForm((f) => ({ ...f, email: e.target.value }))} required />
          </div>
          <div>
            <label className="label text-xs">Phone</label>
            <input type="tel" className="input-field py-1.5 text-sm" placeholder="0400 000 000" value={rosterForm.phone}
              onChange={(e) => setRosterForm((f) => ({ ...f, phone: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={adding} className="btn-primary text-sm disabled:opacity-50">
            {adding ? 'Adding…' : 'Add Team Member'}
          </button>
        </div>
      </form>

      {teamRoster.length === 0 ? (
        <div className="card p-8 text-center text-sm text-slate-400">
          No team members yet. Add someone above.
        </div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {teamRoster.map((m) => (
            <div key={m.id} className="flex items-center gap-4 px-5 py-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-slate-500">{m.firstName?.[0]?.toUpperCase() || '?'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700">{m.firstName} {m.lastName || ''}</p>
                <p className="text-xs text-slate-400">{m.email}{m.phone ? ` · ${m.phone}` : ''}</p>
              </div>
              <button onClick={() => handleRemoveRoster(m)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Entry History Tab ────────────────────────────────────────────────────────

const EntryHistoryTab = ({ currentUser, spaces, bookings }) => {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    const loadEntries = async () => {
      try {
        const q = query(
          collection(db, 'accessLogs'),
          where('userId', '==', currentUser.uid),
        );
        const snap = await getDocs(q);
        const logs = snap.docs
          .map((d) => d.data())
          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setEntries(logs);
      } catch {
        setEntries([]);
      }
      setLoading(false);
    };
    loadEntries();
  }, [currentUser]);

  // Group entries by date
  const grouped = {};
  (entries || []).forEach((e) => {
    const date = e.timestamp ? format(new Date(e.timestamp), 'EEEE, d MMMM yyyy') : 'Unknown Date';
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(e);
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-800">Entry History</h2>
        <p className="text-sm text-slate-500">Your door access log — showing when you've entered the building.</p>
      </div>

      {loading ? (
        <div className="card p-8 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
            <span className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
            Loading entry history...
          </div>
        </div>
      ) : !entries || entries.length === 0 ? (
        <div className="card p-8 text-center text-sm text-slate-400">
          No door entries recorded yet. Entries appear here after you use your PIN to access the building.
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-slate-400">{entries.length} total entries</p>
          {Object.entries(grouped).map(([date, logs]) => {
            return (
              <div key={date}>
                <h3 className="text-sm font-semibold text-slate-600 mb-2">{date}</h3>
                <div className="card divide-y divide-gray-100">
                  {logs.map((log, i) => {
                    const booking = log.bookingId ? bookings.find((b) => b.id === log.bookingId) : null;
                    const space = booking ? spaces.find((s) => s.id === booking.spaceId) : null;
                    const time = log.timestamp ? format(new Date(log.timestamp), 'h:mm a') : '—';

                    return (
                      <div key={i} className="flex items-center gap-3 px-4 py-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-700">{time}</span>
                            {log.door && <span className="text-xs text-slate-400">· {log.door}</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {space && (
                              <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: space.bgColor, color: space.color }}>
                                {space.name}
                              </span>
                            )}
                            {booking?.seatName && <span className="text-xs text-slate-400">{booking.seatName}</span>}
                            {log.credential && (
                              <span className="text-xs text-slate-400">
                                via {log.credential === 'PIN' || log.credential === 'pincode' ? 'PIN' : log.credential}
                              </span>
                            )}
                          </div>
                        </div>
                        {log.actorName && log.actorName !== 'N/A' && (
                          <span className="text-xs text-slate-400 flex-shrink-0">{log.actorName}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function DashboardPage() {
  const { currentUser, userProfile, logout, refreshProfile } = useAuth();
  const spaces = useSpaces();
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [manualBlocks, setManualBlocks] = useState([]);
  const [wifiProfile, setWifiProfile] = useState(null);
  const [credits, setCredits] = useState(null);
  const [subMembers, setSubMembers] = useState([]);
  const [teamRoster, setTeamRoster] = useState([]);
  const [guestBookings, setGuestBookings] = useState({});
  const [accessCodesEnabled, setAccessCodesEnabled] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const [data, blocks, profilesSnap, creditBalance, subs, roster, accessEnabled, nPrefs] = await Promise.all([
          getUserBookings(currentUser.uid),
          currentUser.email ? getManualBlocksForUser(currentUser.email).catch(() => []) : Promise.resolve([]),
          getDoc(doc(db, 'config', 'wifiProfiles')).catch(() => null),
          getMemberCreditBalance(currentUser.uid).catch(() => null),
          getSubMembers(currentUser.uid).catch(() => []),
          getTeamRoster(currentUser.uid).catch(() => []),
          getAccessFeatureEnabled().catch(() => false),
          getNotificationPrefs(currentUser.uid).catch(() => DEFAULT_PREFS),
        ]);
        setBookings(data);
        setManualBlocks(blocks);
        setCredits(creditBalance);
        setSubMembers(subs);
        setTeamRoster(roster);
        setAccessCodesEnabled(accessEnabled);
        setNotifPrefs(nPrefs);
        if (profilesSnap?.exists() && userProfile?.wifiProfileId) {
          const profiles = profilesSnap.data().profiles || [];
          const assigned = profiles.find((p) => p.id === userProfile.wifiProfileId);
          if (assigned) setWifiProfile(assigned);
        }
      } catch (err) {
        console.error('[Firestore] getUserBookings failed:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser, userProfile]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    const booking = bookings.find((b) => b.id === id);
    try {
      await cancelBooking(id);
      setBookings((prev) => prev.map((b) => b.id === id ? { ...b, status: 'cancelled' } : b));
      toast.success('Booking cancelled.');
      // Send cancellation email
      if (booking?.email && currentUser?.email) {
        const startD = booking.startDate?.toDate ? booking.startDate.toDate() : new Date(booking.startDate);
        sendCancellationEmail({
          to: booking.email || currentUser.email,
          name: `${booking.firstName || ''} ${booking.lastName || ''}`.trim() || currentUser.displayName || '',
          spaceName: booking.spaceId,
          startDate: format(startD, 'd MMM yyyy'),
        }).catch(() => {});
      }
    } catch {
      setBookings((prev) => prev.map((b) => b.id === id ? { ...b, status: 'cancelled' } : b));
      toast.success('Booking cancelled.');
    }
  };

  const now = new Date();
  const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
  const active = bookings.filter((b) => {
    if (b.status !== 'confirmed') return false;
    const end = new Date(b.endDate?.toDate ? b.endDate.toDate() : b.endDate);
    // For hourly/half-day bookings keep visible until end of that day
    const effectiveEnd = b.pricingType === 'hourly' || b.pricingType === 'half-day'
      ? new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59)
      : end;
    return effectiveEnd >= todayStart;
  });
  const past = bookings.filter((b) => {
    if (b.status === 'cancelled') return true;
    if (b.status !== 'confirmed') return false;
    const end = new Date(b.endDate?.toDate ? b.endDate.toDate() : b.endDate);
    const effectiveEnd = b.pricingType === 'hourly' || b.pricingType === 'half-day'
      ? new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59)
      : end;
    return effectiveEnd < todayStart;
  });
  const activeBlocks = manualBlocks.filter((bl) => new Date(bl.endDate) >= now);

  // Load guest bookings for all active bookings with team support
  useEffect(() => {
    if (!active.length || !spaces.length) return;
    const teamBookings = active.filter((b) => {
      const sp = spaces.find((s) => s.id === b.spaceId);
      return (sp?.maxSubMembers || 0) > 0;
    });
    teamBookings.forEach((b) => {
      getGuestBookings(b.id).then((guests) => {
        setGuestBookings((prev) => ({ ...prev, [b.id]: guests }));
      }).catch(() => {});
    });
  }, [active.length, spaces.length]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Profile state ──────────────────────────────────────────────────────────
  const [newEmail, setNewEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);

  const [newPhone, setNewPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    if (!newEmail) return;
    setEmailSaving(true);
    try {
      await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
      toast.success('Verification email sent to ' + newEmail + '. Click the link to confirm your new address.');
      setNewEmail('');
    } catch (err) {
      toast.error(err.message || 'Failed to send verification email.');
    }
    setEmailSaving(false);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!newPhone) return;
    setPhoneSaving(true);
    try {
      const normalised = normalisePhone(newPhone);
      const { token } = await sendPhoneOtp({ uid: currentUser.uid, phone: normalised });
      setOtpToken(token);
      setOtpSent(true);
      toast.success('Verification code sent.');
    } catch (err) {
      toast.error(err.message || 'Failed to send code.');
    }
    setPhoneSaving(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode) return;
    setPhoneSaving(true);
    try {
      const { phone } = await verifyPhoneOtp({ token: otpToken, code: otpCode });
      toast.success('Phone number updated.');
      setNewPhone('');
      setOtpCode('');
      setOtpSent(false);
      await updateDoc(doc(db, 'users', currentUser.uid), { phone });
      refreshProfile();
    } catch (err) {
      toast.error(err?.message?.includes('Invalid') ? 'Incorrect code — please try again.' : 'Verification failed.');
    }
    setPhoneSaving(false);
  };

  // Show team tab if any space supports sub-members
  const hasTeamSpaces = spaces.some((s) => (s.maxSubMembers || 0) > 0);

  const tabs = [
    { id: 'bookings', label: 'My Bookings' },
    { id: 'invoices', label: 'Invoices' },
    ...(hasTeamSpaces ? [{ id: 'team', label: 'My Team' }] : []),
    ...(accessCodesEnabled ? [{ id: 'entry-log', label: 'Entry History' }] : []),
    { id: 'profile', label: 'Profile' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-800">
              Welcome, {userProfile?.firstName || currentUser?.displayName?.split(' ')[0] || 'Member'}
            </h1>
            <p className="text-slate-500 text-sm mt-1">{currentUser?.email}</p>
          </div>
          <Link to="/book" className="btn-primary self-start sm:self-auto">+ New Booking</Link>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Active', value: active.length, color: 'text-green-600' },
            { label: 'Total Bookings', value: bookings.length, color: 'text-slate-800' },
            { label: 'Pending', value: bookings.filter((b) => b.status === 'pending').length, color: 'text-amber-600' },
            {
              label: 'Credits Available',
              value: credits?.available != null ? `${credits.available} hrs` : '—',
              color: credits?.available > 0 ? 'text-emerald-600' : 'text-slate-400',
            },
          ].map((stat) => (
            <div key={stat.label} className="card p-4 text-center">
              <div className={`text-2xl font-extrabold ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-slate-400 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* WiFi card */}
        <div className="card p-4 mb-6 flex items-center gap-4">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Wi-Fi</p>
            {wifiProfile ? (
              <p className="text-sm text-slate-700">
                <strong>{wifiProfile.ssid}</strong>
                {wifiProfile.password && <span className="text-slate-500"> · {wifiProfile.password}</span>}
              </p>
            ) : (
              <p className="text-sm text-slate-500">Contact us for Wi-Fi details.</p>
            )}
          </div>
          {wifiProfile && (
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-slate-400">{wifiProfile.name}</p>
            </div>
          )}
        </div>

        {/* Profile card */}
        <div className="card p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-xl flex-shrink-0">
            {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'M'}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800">{currentUser?.displayName || 'Member'}</h3>
            <p className="text-sm text-slate-400">{currentUser?.email}</p>
            {userProfile?.company && <p className="text-xs text-slate-400 mt-0.5">{userProfile.company}</p>}
            {userProfile?.phone && <p className="text-xs text-slate-400 mt-0.5">{userProfile.phone}</p>}
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto">
            <Link to="/support" className="text-sm text-slate-400 hover:text-slate-600 font-medium">Support</Link>
            <button onClick={logout} className="text-sm text-red-500 hover:text-red-700 font-medium">
              Sign Out
            </button>
          </div>
        </div>

        {/* Tabs */}
        {tabs.length > 1 && (
          <div className="overflow-x-auto -mx-4 px-4 mb-6">
            <div className="flex gap-1 border-b border-gray-200 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-brand-500 text-brand-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bookings tab */}
        {activeTab === 'bookings' && (
          <>
            <section className="mb-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">Active & Upcoming</h2>
              {loading ? (
                <div className="flex items-center gap-2 p-4">
                  <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-slate-400">Loading bookings…</span>
                </div>
              ) : active.length === 0 && activeBlocks.length === 0 ? (
                <div className="card p-8 text-center">
                  <p className="text-slate-400 text-sm mb-4">No active bookings.</p>
                  <Link to="/book" className="btn-primary text-sm">Book a Space</Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {active.map((b) => <BookingCard key={b.id} booking={b} onCancel={handleCancel} spaces={spaces}
                    accessCodesEnabled={accessCodesEnabled} teamRoster={teamRoster}
                    guestBookings={guestBookings} setGuestBookings={setGuestBookings}
                    wifiProfile={wifiProfile} userProfile={userProfile} currentUser={currentUser}
                    setActiveTab={setActiveTab} />)}
                  {activeBlocks.map((bl) => {
                    const space = spaces.find((s) => s.id === bl.spaceId);
                    return (
                      <div key={bl.id} className="card p-5">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                          <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: space?.bgColor || '#f1f5f9' }}>
                            <span className="text-lg" style={{ color: space?.color }}>📋</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-800">{space?.name || bl.spaceId}</h3>
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Reserved</span>
                            </div>
                            <p className="text-sm text-slate-500">{bl.startDate} → {bl.endDate}</p>
                            {bl.seatName && <p className="text-xs text-slate-400 mt-0.5">{bl.seatName}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {past.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">Past & Cancelled</h2>
                <div className="space-y-3">
                  {past.map((b) => <BookingCard key={b.id} booking={b} onCancel={handleCancel} spaces={spaces} />)}
                </div>
              </section>
            )}
          </>
        )}

        {/* Invoices tab */}
        {activeTab === 'invoices' && (() => {
          const invoicedBookings = bookings.filter((b) => b.invoiceNumber || b.creditNoteNumber);
          return (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Invoices & Credit Notes</h2>
                <p className="text-sm text-slate-500">Tax invoices and credit notes for your bookings.</p>
              </div>

              {loading ? (
                <div className="card p-8 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
                    <span className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
                    Loading invoices...
                  </div>
                </div>
              ) : invoicedBookings.length === 0 ? (
                <div className="card p-8 text-center text-sm text-slate-400">
                  No invoices yet. Invoices will appear here once they have been generated for your bookings.
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden sm:block card overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 text-left">
                          <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Invoice #</th>
                          <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Date</th>
                          <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Space</th>
                          <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400 text-right">GST</th>
                          <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400 text-right">Total (inc GST)</th>
                          <th className="px-5 py-3"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {invoicedBookings.flatMap((b) => {
                          const space = spaces.find((s) => s.id === b.spaceId);
                          const invoiceDate = b.invoiceGeneratedAt?.toDate
                            ? b.invoiceGeneratedAt.toDate()
                            : b.invoiceGeneratedAt ? new Date(b.invoiceGeneratedAt) : null;
                          const totalIncGst = (b.exGstAmount || 0) + (b.gstAmount || 0);
                          const rows = [];
                          if (b.invoiceNumber) {
                            rows.push(
                              <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-5 py-3 font-medium text-slate-700">{b.invoiceNumber}</td>
                                <td className="px-5 py-3 text-slate-500">{invoiceDate ? format(invoiceDate, 'd MMM yyyy') : '—'}</td>
                                <td className="px-5 py-3 text-slate-500">{space?.name || b.spaceId}</td>
                                <td className="px-5 py-3 text-slate-500 text-right">${(b.gstAmount || 0).toFixed(2)}</td>
                                <td className="px-5 py-3 font-medium text-slate-700 text-right">${totalIncGst.toFixed(2)}</td>
                                <td className="px-5 py-3 text-right">
                                  {b.invoiceUrl ? (
                                    <a href={b.invoiceUrl} target="_blank" rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-brand-500 hover:text-brand-700 font-medium text-xs">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                      Download
                                    </a>
                                  ) : (
                                    <span className="text-xs text-slate-300">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          }
                          if (b.creditNoteNumber) {
                            const cnDate = b.creditNoteGeneratedAt?.toDate
                              ? b.creditNoteGeneratedAt.toDate()
                              : b.creditNoteGeneratedAt ? new Date(b.creditNoteGeneratedAt) : null;
                            rows.push(
                              <tr key={`${b.id}-cn`} className="hover:bg-gray-50 transition-colors bg-red-50/50">
                                <td className="px-5 py-3 font-medium text-red-600">{b.creditNoteNumber}</td>
                                <td className="px-5 py-3 text-slate-500">{cnDate ? format(cnDate, 'd MMM yyyy') : '—'}</td>
                                <td className="px-5 py-3 text-slate-500">{space?.name || b.spaceId} <span className="text-xs text-red-500">(credit note)</span></td>
                                <td className="px-5 py-3 text-red-500 text-right">-${(b.gstAmount || 0).toFixed(2)}</td>
                                <td className="px-5 py-3 font-medium text-red-600 text-right">-${totalIncGst.toFixed(2)}</td>
                                <td className="px-5 py-3 text-right">
                                  {b.creditNoteUrl ? (
                                    <a href={b.creditNoteUrl} target="_blank" rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-brand-500 hover:text-brand-700 font-medium text-xs">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                      Download
                                    </a>
                                  ) : <span className="text-xs text-slate-300">—</span>}
                                </td>
                              </tr>
                            );
                          }
                          return rows;
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="sm:hidden space-y-3">
                    {invoicedBookings.flatMap((b) => {
                      const space = spaces.find((s) => s.id === b.spaceId);
                      const invoiceDate = b.invoiceGeneratedAt?.toDate
                        ? b.invoiceGeneratedAt.toDate()
                        : b.invoiceGeneratedAt ? new Date(b.invoiceGeneratedAt) : null;
                      const totalIncGst = (b.exGstAmount || 0) + (b.gstAmount || 0);
                      const cards = [];
                      if (b.invoiceNumber) {
                        cards.push(
                          <div key={b.id} className="card p-5">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-semibold text-slate-800">{b.invoiceNumber}</p>
                                <p className="text-xs text-slate-400">{invoiceDate ? format(invoiceDate, 'd MMM yyyy') : '—'}</p>
                              </div>
                              {b.invoiceUrl && (
                                <a href={b.invoiceUrl} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-brand-500 hover:text-brand-700 font-medium text-xs">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  Download
                                </a>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mb-2">{space?.name || b.spaceId}</p>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-slate-400">GST: ${(b.gstAmount || 0).toFixed(2)}</span>
                              <span className="font-medium text-slate-700">Total: ${totalIncGst.toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      }
                      if (b.creditNoteNumber) {
                        const cnDate = b.creditNoteGeneratedAt?.toDate
                          ? b.creditNoteGeneratedAt.toDate()
                          : b.creditNoteGeneratedAt ? new Date(b.creditNoteGeneratedAt) : null;
                        cards.push(
                          <div key={`${b.id}-cn`} className="card p-5 border-red-200 bg-red-50/30">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-semibold text-red-600">{b.creditNoteNumber}</p>
                                <p className="text-xs text-slate-400">{cnDate ? format(cnDate, 'd MMM yyyy') : '—'}</p>
                              </div>
                              {b.creditNoteUrl && (
                                <a href={b.creditNoteUrl} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-brand-500 hover:text-brand-700 font-medium text-xs">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  Download
                                </a>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mb-2">{space?.name || b.spaceId} <span className="text-xs text-red-500">(credit note)</span></p>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-red-400">GST: -${(b.gstAmount || 0).toFixed(2)}</span>
                              <span className="font-medium text-red-600">Credit: -${totalIncGst.toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      }
                      return cards;
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Team roster tab */}
        {activeTab === 'team' && (
          <TeamRosterTab
            currentUser={currentUser}
            teamRoster={teamRoster}
            setTeamRoster={setTeamRoster}
            setGuestBookings={setGuestBookings}
          />
        )}

        {/* Entry History tab */}
        {activeTab === 'entry-log' && (
          <EntryHistoryTab currentUser={currentUser} spaces={spaces} bookings={bookings} />
        )}

        {/* Profile tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6 max-w-lg">

            {/* Email */}
            <div className="card p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-slate-800">Email Address</h3>
                <p className="text-sm text-slate-500 mt-0.5">Current: <span className="font-medium text-slate-700">{currentUser?.email}</span></p>
              </div>
              <form onSubmit={handleEmailUpdate} className="space-y-3">
                <div>
                  <label className="label text-sm">New email address</label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="new@example.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">We'll send a verification link to the new address. Your email won't change until you click it.</p>
                </div>
                <div className="flex justify-end">
                  <button type="submit" disabled={emailSaving || !newEmail} className="btn-primary text-sm disabled:opacity-50">
                    {emailSaving ? 'Sending…' : 'Send Verification Email'}
                  </button>
                </div>
              </form>
            </div>

            {/* Phone */}
            <div className="card p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-slate-800">Phone Number</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Current: <span className="font-medium text-slate-700">{userProfile?.phone || 'Not set'}</span>
                </p>
                <p className="text-xs text-slate-400 mt-1">Optional — add your number to receive SMS updates about your bookings.</p>
              </div>

              {!otpSent ? (
                <form onSubmit={handleSendOtp} className="space-y-3">
                  <div>
                    <label className="label text-sm">New phone number</label>
                    <input
                      type="tel"
                      className="input-field"
                      placeholder="04XX XXX XXX or +61 4XX XXX XXX"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex justify-end">
                    <button type="submit" disabled={phoneSaving || !newPhone} className="btn-primary text-sm disabled:opacity-50">
                      {phoneSaving ? 'Sending…' : 'Send Verification Code'}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-3">
                  <p className="text-sm text-slate-600">Enter the 6-digit code sent to <span className="font-medium">{newPhone}</span>.</p>
                  <div>
                    <label className="label text-sm">Verification code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="input-field tracking-widest text-center text-lg font-mono"
                      placeholder="000000"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      required
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={() => { setOtpSent(false); setOtpCode(''); setOtpToken(''); }} className="text-sm text-slate-400 hover:text-slate-600 underline">
                      Change number
                    </button>
                    <button type="submit" disabled={phoneSaving || otpCode.length < 6} className="btn-primary text-sm disabled:opacity-50">
                      {phoneSaving ? 'Verifying…' : 'Verify & Save'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Notification Preferences */}
            <div className="card p-5 space-y-4">
              <div>
                <h3 className="font-semibold text-slate-800 mb-0.5">Notification Preferences</h3>
                <p className="text-xs text-slate-400">Choose how you'd like to be contacted.</p>
              </div>

              {/* Service notifications */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Service Notifications</p>
                <div className="space-y-3">
                  {Object.entries(PREF_LABELS).filter(([, v]) => v.type === 'service').map(([key, meta]) => (
                    <div key={key} className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">{meta.label}</p>
                        <p className="text-xs text-slate-400">{meta.description}</p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifPrefs[key]?.email ?? true}
                            onChange={(e) => {
                              const updated = { ...notifPrefs, [key]: { ...notifPrefs[key], email: e.target.checked } };
                              setNotifPrefs(updated);
                              saveNotificationPrefs(currentUser.uid, updated).catch(() => {});
                            }}
                            className="w-4 h-4 accent-brand-500"
                          />
                          <span className="text-xs text-slate-500">Email</span>
                        </label>
                        {notifPrefs[key]?.sms !== undefined && (
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={notifPrefs[key]?.sms ?? true}
                              onChange={(e) => {
                                const updated = { ...notifPrefs, [key]: { ...notifPrefs[key], sms: e.target.checked } };
                                setNotifPrefs(updated);
                                saveNotificationPrefs(currentUser.uid, updated).catch(() => {});
                              }}
                              className="w-4 h-4 accent-brand-500"
                            />
                            <span className="text-xs text-slate-500">SMS</span>
                          </label>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Marketing notifications */}
              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Marketing</p>
                {Object.entries(PREF_LABELS).filter(([, v]) => v.type === 'marketing').map(([key, meta]) => (
                  <div key={key} className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">{meta.label}</p>
                      <p className="text-xs text-slate-400">{meta.description}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifPrefs[key]?.email ?? false}
                          onChange={(e) => {
                            const updated = { ...notifPrefs, [key]: { ...notifPrefs[key], email: e.target.checked } };
                            setNotifPrefs(updated);
                            saveNotificationPrefs(currentUser.uid, updated).catch(() => {});
                          }}
                          className="w-4 h-4 accent-brand-500"
                        />
                        <span className="text-xs text-slate-500">Email</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
